import type { AccuracyStudy } from './accuracyLedger';

/* The meta description for an accuracy card.
 *
 * This exists because the previous one-line template asserted a clinical
 * comparison that four cards never made. It read:
 *
 *   `${figure}${label}, measured against ${study.reference ?? 'a clinical
 *    reference'} across ${study.sampleSize}.`
 *
 * and the `??` is the whole problem. Four of the fifty-five cards have no
 * reference BY DESIGN — steps-notwalking's own body says "No reference device
 * is needed here", because the true step count while seated is zero and there
 * is nothing to compare against. The template answered that by inventing one,
 * in the single line an AI search surface quotes first.
 *
 * That is the claim shape the Clarkson complaint against Oura attacks, written
 * by us, about a comparison that did not happen. So the rule here is absolute:
 * NO REFERENCE MEANS NO CLAUSE. Never default, never soften — omit.
 */

/* Prose forms, mapped rather than derived.
 *
 * The labels are display strings — "Consumer smartwatch", "ECG chest strap" —
 * and dropping them mid-sentence produced "measured against Validated
 * smartwatch". Fixing that needs an article and a lower-casing, and neither is
 * mechanical: it is "a consumer smartwatch" but "an ECG chest strap" and
 * "the OvuSense reference sensor", and the lower-casing has to leave ECG, FDA
 * and OvuSense alone. Seventeen entries, written out, is smaller than the
 * rules that would get them wrong. */
const REFERENCE_PROSE: Record<string, string> = {
  'ECG chest strap': 'an ECG chest strap',
  '12-lead ECG': 'a 12-lead ECG',
  'FDA-cleared home sleep test (EEG)': 'an FDA-cleared home sleep test (EEG)',
  'Consumer smartwatch': 'a consumer smartwatch',
  'FDA-cleared sleep device': 'an FDA-cleared sleep device',
  'Laboratory thermometer': 'a laboratory thermometer',
  'Clinical pulse oximeter': 'a clinical pulse oximeter',
  'Multi-metabolite ovulation test': 'a multi-metabolite ovulation test',
  'OvuSense reference sensor': 'the OvuSense reference sensor',
  'Laboratory exercise test': 'a laboratory exercise test',
  'Laboratory blood work': 'laboratory blood work',
  'Reference blood glucose': 'a reference blood glucose measurement',
  'The day the period arrived': 'the day the period arrived',
  'Wearer confirmation': 'wearer confirmation',
  'Ovulation tests logged in the app': 'ovulation tests logged in the app',
  'Illness episodes logged in the app': 'illness episodes logged in the app',
  'Hand tally counter': 'a hand tally counter',
};

/* Hand-written descriptions for the cards the template cannot serve, from the
   copy QC. Each one is a card whose finding is not "X% against a reference" —
   steps-notwalking is sixty seated recordings with no false step, and there is
   no percentage-against-anything sentence that describes it honestly. Authored
   copy, so it lives in one visible list rather than being derived. */
const AUTHORED: Record<string, string> = {
  'steps-notwalking':
    '100% — When you’re seated: 60 seated recordings, and not one false step, across 15 participants.',
  'steps-car':
    '98% — Commuting: 98% of the real walking step rate not counted in a car, across 14 participants.',
  'longevity-bloodage':
    '10.2 bpm — Blood Age: the resting heart-rate gap between the fastest- and slowest-ageing, at matched age and sex, in 20,858 adults.',
  'longevity-cardioage':
    '1.2 yrs — Cardio Age: typical drift across a full year in 442 adults, against 3.2 to 3.5 years when fitness genuinely changes.',
  /* The qualifier leads rather than trails. At 174 characters the original
     was cut by Google at ~160 exactly where "in the manufacturers' own
     published trials" began — turning a sentence about somebody else's trial
     of somebody else's sensor into an unqualified Ultrahuman accuracy claim.
     Front-loading it means a truncation can no longer produce that reading. */
  'metabolic-sensor':
    '8.7% — average difference from a lab blood test, in the manufacturers’ own published trials of the two third-party sensors the M1 and M2 read.',
  'steps-walks':
    '95.2% on an indoor walk and 91.8% outdoors — step count measured against a hand tally count across 22 participants.',
  /* Joseph v. Oura (filed 22 Sep 2026) goes after sleep-accuracy claims, so
     every sleep-stage figure has to read as an estimate. The template cannot
     produce this: the hedge goes BEFORE the number, "agreement" replaces the
     bare percentage, and "measured against" becomes "against" — a reference
     the ring is compared to, not a measurement it makes. Authored rather than
     templated for the same reason the five above are. */
  'sleep-stages':
    'An estimated 62% agreement — Sleep stages, against an FDA-cleared home sleep test (EEG) across 23 paired nights.',
};

/* `sampleSize` is a display string built for the card, where "Based on 2
   sensors" stands on its own line and reads correctly. Inside a sentence it
   produced "across Based on 2 sensors". */
/* Exported because the ledger index renders the same sentence for crawlers and
   screen readers, and was building it from the raw display strings: "measured
   against Hand tally counter, Based on 2 sensors". The prose forms already
   existed here for the meta description; there is no second opinion to have
   about how a reference reads mid-sentence. */
export const asClause = (sampleSize: string): string =>
  sampleSize.replace(/^Based on\s+/i, '');

/* A metric label as it reads inside a sentence.
 *
 * The ledger writes them sentence case for a table column — "Heart rate
 * agreement" — and mid-sentence that capital is wrong. Only the first
 * character drops, and only when the first WORD is not an acronym: two of the
 * twenty-nine are "HRV agreement" and "VO₂max agreement", and lower-casing
 * those makes them unreadable rather than merely lower-case. */
export const metricProse = (metric: string): string =>
  /^[A-Z]{2,}|^VO/.test(metric) ? metric : metric[0].toLowerCase() + metric.slice(1);

/** A reference display label as it reads inside a sentence. */
export const referenceProse = (reference: string): string =>
  REFERENCE_PROSE[reference] ?? reference.toLowerCase();

export const accuracyDescription = (
  /* Pick<> rather than the full study: the /science/studies/ hub reuses this
     for its Dataset stubs, and its flattened rows omit `activity`. Only these
     four fields are ever read. */
  study: Pick<
    AccuracyStudy,
    'slug' | 'label' | 'reference' | 'sampleSize' | 'figureLabel'
  >,
  figure: string,
): string => {
  const authored = AUTHORED[study.slug];
  if (authored) return authored;

  const reference = study.reference
    ? referenceProse(study.reference)
    : undefined;

  /* "97% agreement — Total sleep, …" where the editor chose it. The brief
     asks for the word in the description as well as on the card, because the
     description is what a search surface quotes — and quoting "97%" bare is
     the claim being walked back. `figure` arrives as "97% — ", so the word
     goes inside it, before the dash. */
  const head =
    study.figureLabel === 'agreement'
      ? figure.replace(/\s*—\s*$/, ' agreement — ')
      : figure;

  const parts = [`${head}${study.label}`];
  if (reference) parts.push(`measured against ${reference}`);
  if (study.sampleSize) parts.push(`across ${asClause(study.sampleSize)}`);

  /* Two clauses join with a comma; one stands alone. Without this a card with
     no reference read "100% — When you're seated, across 15 participants." */
  return `${parts[0]}${parts.length > 1 ? `, ${parts.slice(1).join(' ')}` : ''}.`;
};

/* Dataset JSON-LD for one accuracy card.
 *
 * The 52 cards previously carried only the site-wide Organization and WebSite
 * blocks, so an AI surface parsing structured data found no accuracy claim on
 * the site to cite — which, for a section whose stated purpose is being
 * quotable, is the whole thing missing.
 *
 * Dataset rather than ScholarlyArticle because the canonical build already
 * ships Dataset for these, and matching it means the two agree when the
 * canonical version lands rather than competing.
 *
 * EVERY FIELD COMES FROM DATA. Nothing is defaulted and nothing is inferred:
 * a card with no reference gets no comparison, a card with no citation gets no
 * citation, a "coming soon" card with no figure gets no measured value. That
 * is the same rule as the meta description above, and for the same reason —
 * this file exists because the previous template invented a clinical
 * comparison, and structured data is read by machines that cannot tell an
 * invented field from a measured one.
 *
 * No `distribution`: there is no downloadable file behind these, and claiming
 * one to satisfy a rich-result checklist would be the same class of mistake.
 */
export const accuracyDataset = (
  study: AccuracyStudy,
  description: string,
): Record<string, unknown> => {
  const url = `https://www.ultrahuman.com/science/accuracy/${study.slug}/`;
  const src = study.activity.src;

  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    /* Same @id the /science/studies/ DataCatalog references, so the catalogue
       entry and this page are one node rather than two descriptions of the
       same study. See components/science/studies/collectionSeo.ts. */
    '@id': url,
    name: study.activity.title || study.label,
    description,
    url,
    /* @id joins the root-layout Organization node (app/layout.tsx) so parsers
       resolve creator/publisher to one entity instead of an anonymous copy. */
    creator: {
      '@type': 'Organization',
      '@id': 'https://www.ultrahuman.com/#organization',
      name: 'Ultrahuman',
    },
    publisher: {
      '@type': 'Organization',
      '@id': 'https://www.ultrahuman.com/#organization',
      name: 'Ultrahuman',
    },
    isAccessibleForFree: true,
    /* Back-reference to the hub's DataCatalog (@id defined in
       components/science/studies/collectionSeo.ts), closing the graph both
       ways: catalog → dataset via `dataset`, dataset → catalog via this. */
    includedInDataCatalog: {
      '@type': 'DataCatalog',
      '@id': 'https://www.ultrahuman.com/science/studies/#accuracy',
      name: 'Accuracy studies',
      url: 'https://www.ultrahuman.com/science/studies/',
    },
    keywords: [study.areaLabel, study.bucketLabel, study.metric].filter(
      Boolean,
    ),
    /* The measured claim itself, as a value a machine can read rather than a
       number buried in a sentence. */
    ...(study.figure && study.metric
      ? {
          variableMeasured: {
            '@type': 'PropertyValue',
            name: study.metric,
            value: study.figure,
          },
        }
      : {}),
    ...(study.sensors.length
      ? { measurementTechnique: study.sensors.map((x) => x.label) }
      : {}),
    /* What it was checked against — omitted, never defaulted, where the study
       has no external reference. */
    ...(study.reference ? { comparativeTo: study.reference } : {}),
    ...(src
      ? {
          citation: {
            '@type': 'CreativeWork',
            name: src.t,
            ...(src.u ? { url: src.u } : {}),
          },
        }
      : {}),
    about: { '@type': 'Thing', name: study.areaLabel },
  };
};
